import React, { useContext, useState, useEffect } from 'react';
import { LanguageContext } from '../contexts/LanguageContext';
import SEO from './SEO';
import { loadBlogContent } from '../utils/preloadBlogContent';
import './Blog.css';

export default function Blog() {
    const { locale } = useContext(LanguageContext);
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        let mounted = true;
        
        const loadContent = async () => {
            const data = await loadBlogContent(locale);
            
            if (mounted) {
                setContent(data);
                setLoading(false);
            }
        };
        
        loadContent();
        
        return () => {
            mounted = false;
        };
    }, [locale]);
    
    if (loading || !content) {
        return <div className="blog-container"><div className="loading">Loading...</div></div>;
    }
    
    return (
        <>
            <SEO 
                title={`${content.title} | PPE SCOPE`}
                description={content.metaDescription}
                keywords={content.keywords}
                canonical={`https://ppe-scope.ch/blog/`}
                ogImage={`https://ppe-scope.ch/assets/blog/blog-og-image.webp`}
                ogImagePng={`https://ppe-scope.ch/assets/blog/blog-og-image.png`}
                ogType="article"
                includeHreflang={true}
                pagePath="/blog"
            />
            
            <div className="blog-container">
                <article className="post post--editorial">
                    <div className="blog-header">
                        <h1>{content.fullTitle || content.title}</h1>
                        <div className="social-share">
                            <span className="share-label">Share:</span>
                            <a 
                                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent('https://ppe-scope.ch/blog/')}&text=${encodeURIComponent(content.fullTitle || content.title)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-btn social-btn--twitter"
                                aria-label="Share on Twitter"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                            </a>
                            <a 
                                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://ppe-scope.ch/blog/')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-btn social-btn--linkedin"
                                aria-label="Share on LinkedIn"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                            </a>
                            <a 
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://ppe-scope.ch/blog/')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="social-btn social-btn--facebook"
                                aria-label="Share on Facebook"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                            </a>
                        </div>
                    </div>

                    <p>{content.intro.p1}</p>
                    <p>{content.intro.p2}</p>
                    <p>{content.intro.p3}</p>

                    <h2>{content.sections.potential.heading}</h2>
                    <p>{content.sections.potential.p1}</p>
                    <p>{content.sections.potential.p2}</p>
                    <p>
                        {content.sections.potential.p3.split('Our comprehensive assessment combines both psychometric evaluation and practical skill measurement to provide actionable insights.')[0]}
                        <a className="link link--internal" href="https://ppe-scope.ch/">
                            <span className="anchor anchor--internal">Our comprehensive assessment</span>
                        </a>
                        {content.sections.potential.p3.split('Our comprehensive assessment combines both psychometric evaluation and practical skill measurement to provide actionable insights.')[1]}
                    </p>

                    <h2>{content.sections.typing.heading}</h2>
                    <p>{content.sections.typing.p1}</p>
                    <p>{content.sections.typing.p2}</p>
                    <ul>
                        {content.sections.typing.list.map((item, index) => (
                            <li key={index}><span className="term">{item}</span></li>
                        ))}
                    </ul>
                    <p>{content.sections.typing.p3}</p>
                    <p>{content.sections.typing.p4}</p>

                    <h2>{content.sections.metrics.heading}</h2>
                    <p>{content.sections.metrics.p1}</p>
                    <p>{content.sections.metrics.p2}</p>
                    <ul>
                        {content.sections.metrics.list.map((item, index) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                    <p>{content.sections.metrics.p3}</p>

                    <h2>{content.sections.tools.heading}</h2>
                    <p>{content.sections.tools.p1}</p>
                    <p>
                        {(() => {
                            const text = content.sections.tools.p2;
                            const brandName = "typingmetrics.com";
                            
                            // Language-specific anchor text mapping
                            const anchorTextMap = {
                                en: "typing speed and accuracy training",
                                fr: "l'entraînement à la vitesse et à la précision de frappe",
                                de: "Tippgeschwindigkeit und -genauigkeit",
                                it: "velocità e accuratezza di digitazione",
                                es: "velocidad y precisión de mecanografía"
                            };
                            
                            const anchorText = anchorTextMap[locale] || anchorTextMap.en;
                            
                            // Split the text by the anchor text
                            const parts = text.split(anchorText);
                            if (parts.length < 2) return text;
                            
                            // Further split to handle brand name
                            const afterAnchor = parts[1].split(brandName);
                            
                            return (
                                <>
                                    {parts[0]}
                                    <a className="link link--out" href="https://typingmetrics.com" rel="nofollow noopener noreferrer" target="_blank">
                                        <span className="anchor anchor--primary">{anchorText}</span>
                                    </a>
                                    {afterAnchor[0]}
                                    <span className="brand brand--typing">{brandName}</span>
                                    {afterAnchor[1] || ''}
                                </>
                            );
                        })()}
                    </p>
                    <p>{content.sections.tools.p3}</p>

                    <h2>{content.sections.agency.heading}</h2>
                    <p>{content.sections.agency.p1}</p>
                    <p>
                        {content.sections.agency.p2}{' '}
                        <span className="pull pull--key">{content.sections.agency.quote}</span>
                    </p>
                    <p>{content.sections.agency.p3}</p>
                    <p>
                        {content.sections.agency.p4}{' '}
                        <a className="link link--internal" href="https://ppe-scope.ch/results">
                            <span className="anchor anchor--internal">view your assessment results</span>
                        </a>
                        {' '}to see how psychometric insights translate into practical development opportunities.
                    </p>

                    <p className="post__cta">
                        <span className="label">{content.cta.label}</span>
                        <a className="link link--internal" href="https://ppe-scope.ch/">
                            <span className="anchor anchor--internal">{content.cta.linkText}</span>
                        </a>
                    </p>
                </article>
                
                <footer className="blog-footer">
                    <p>&copy; {new Date().getFullYear()} {content.copyright}</p>
                </footer>
            </div>
        </>
    );
}
